import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  Users, CheckCircle, Clock, XCircle, Building2,
  Search, Check, X, Eye, Lock, Unlock, Calendar, Mail, Hash, Activity
} from 'lucide-react';

const formatDateTimeBR = (isoString) => {
  if (!isoString) return '—';
  const d = isoString instanceof Date ? isoString : new Date(isoString);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Recife'
  });
};

const formatDateBR = (isoString) => {
  if (!isoString) return '—';
  const str = isoString instanceof Date ? isoString.toISOString() : String(isoString);
  const part = str.split('T')[0];
  const [y, m, d] = part.split('-');
  if (!y || !m || !d) return '—';
  return `${d}/${m}/${y}`;
};

const StatusBadge = ({ status }) => {
  const map = {
    aprovado: 'bg-green-100 text-green-700',
    rejeitado: 'bg-red-100 text-red-700',
    pendente: 'bg-amber-100 text-amber-700',
  };
  const label = { aprovado: 'Aprovado', rejeitado: 'Bloqueado', pendente: 'Pendente' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {label[status] || status}
    </span>
  );
};

const InfoRow = ({ icon, label, value, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <div className="flex items-center gap-1.5 text-gray-400 mb-0.5">
      {icon}
      <span className="text-xs">{label}</span>
    </div>
    <div className="text-sm font-medium text-gray-800">{value}</div>
  </div>
);

const DetailModal = ({ item, onClose }) => {
  if (!item) return null;
  const isClinica = item.role === 'clinica';
  const status = isClinica ? item.clinicaStatus : item.crefitoStatus;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isClinica ? 'bg-blue-100' : 'bg-blue-100'}`}>
              {isClinica ? <Building2 className="w-5 h-5 text-blue-600" /> : <Users className="w-5 h-5 text-blue-600" />}
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">{isClinica ? (item.clinicaInfo?.nome || item.nome) : item.nome}</p>
              <p className="text-xs text-gray-400">{isClinica ? 'Detalhes da clínica' : 'Detalhes do fisioterapeuta'}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <InfoRow icon={<Mail size={14} />} label="Email" value={item.email} full />
            {isClinica
              ? <InfoRow icon={<Hash size={14} />} label="CNPJ" value={item.clinicaInfo?.cnpj || '—'} />
              : <InfoRow icon={<Hash size={14} />} label="CREFITO" value={item.crefito || '—'} />
            }
            <InfoRow icon={<Activity size={14} />} label="Status atual" value={<StatusBadge status={status} />} />
            <InfoRow icon={<Calendar size={14} />} label="Cadastrado em" value={formatDateBR(item.createdAt)} />
            <InfoRow icon={<Calendar size={14} />} label="Última alteração" value={item.updatedAt ? formatDateTimeBR(item.updatedAt) : '—'} />
          </div>
          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Histórico de ações</p>
            <ol className="relative border-l border-gray-200 ml-2 space-y-4">
              <li className="ml-4">
                <div className="absolute -left-1.5 w-3 h-3 rounded-full bg-blue-400 border-2 border-white" />
                <p className="text-xs font-medium text-gray-700">Cadastro no sistema</p>
                <p className="text-xs text-gray-400">{formatDateTimeBR(item.createdAt)}</p>
              </li>
              {item.decisaoAt && (
                <li className="ml-4">
                  <div className={`absolute -left-1.5 w-3 h-3 rounded-full border-2 border-white ${status === 'aprovado' ? 'bg-green-500' : 'bg-red-400'}`} />
                  <p className="text-xs font-medium text-gray-700">{status === 'aprovado' ? 'Conta aprovada' : 'Conta bloqueada'}</p>
                  <p className="text-xs text-gray-400">{formatDateTimeBR(item.decisaoAt)}</p>
                </li>
              )}
            </ol>
          </div>
        </div>
        <div className="px-5 pb-5">
          <button onClick={onClose} className="w-full py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">Fechar</button>
        </div>
      </div>
    </div>
  );
};

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('pendentes');
  const [pendingFisioterapeutas, setPendingFisioterapeutas] = useState([]);
  const [historyFisioterapeutas, setHistoryFisioterapeutas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalItem, setModalItem] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [pendingRes, historyRes] = await Promise.all([
        api.get('/admin/fisioterapeutas/pendentes'),
        api.get('/admin/fisioterapeutas/historico')
      ]);
      setPendingFisioterapeutas(pendingRes.data.data);
      setHistoryFisioterapeutas(historyRes.data.data);
    } catch { toast.error('Erro ao carregar dados'); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id) => {
    try {
      await api.patch(`/admin/fisioterapeutas/${id}/aprovar`);
      toast.success('Aprovado!');
      fetchData();
    } catch { toast.error('Erro ao aprovar'); }
  };

  const handleReject = async (id) => {
    try {
      await api.patch(`/admin/fisioterapeutas/${id}/rejeitar`);
      toast.success('Rejeitado');
      fetchData();
    } catch { toast.error('Erro ao rejeitar'); }
  };

  const handleBlock = async (id) => {
    if (!window.confirm('Bloquear esta conta?')) return;
    try {
      await api.patch(`/admin/fisioterapeutas/${id}/rejeitar`);
      toast.success('Conta bloqueada');
      fetchData();
    } catch { toast.error('Erro ao bloquear'); }
  };

  const handleUnblock = async (id) => {
    if (!window.confirm('Desbloquear esta conta?')) return;
    try {
      await api.patch(`/admin/fisioterapeutas/${id}/aprovar`);
      toast.success('Conta desbloqueada');
      fetchData();
    } catch { toast.error('Erro ao desbloquear'); }
  };

  const getStatus = (f) => f.role === 'clinica' ? f.clinicaStatus : f.crefitoStatus;

  const totalAprovados = historyFisioterapeutas.filter(f => getStatus(f) === 'aprovado').length;
  const totalRejeitados = historyFisioterapeutas.filter(f => getStatus(f) === 'rejeitado').length;
  const totalFisios = totalAprovados + totalRejeitados + pendingFisioterapeutas.length;

  const filteredHistory = historyFisioterapeutas.filter(f => {
    const nome = f.role === 'clinica' ? (f.clinicaInfo?.nome || f.nome) : f.nome;
    return (
      nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (f.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {modalItem && <DetailModal item={modalItem} onClose={() => setModalItem(null)} />}

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Painel Administrativo</h1>
        <p className="text-gray-500 mt-1">Gerencie fisioterapeutas e clínicas.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">

          {/* Estatísticas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: <Users className="w-5 h-5 text-blue-600" />, bg: 'bg-blue-50', value: totalFisios, label: 'Cadastrados', sub: 'Total' },
              { icon: <CheckCircle className="w-5 h-5 text-green-600" />, bg: 'bg-green-50', value: totalAprovados, label: 'Aprovados', sub: 'Contas ativas' },
              { icon: <Clock className="w-5 h-5 text-amber-600" />, bg: 'bg-amber-50', value: pendingFisioterapeutas.length, label: 'Pendentes', sub: 'Aguardando análise' },
              { icon: <XCircle className="w-5 h-5 text-red-500" />, bg: 'bg-red-50', value: totalRejeitados, label: 'Bloqueados', sub: 'Contas recusadas' },
            ].map((card) => (
              <div key={card.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${card.bg} flex items-center justify-center`}>{card.icon}</div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{card.value}</p>
                  <p className="text-xs text-gray-500">{card.label}</p>
                  <p className="text-xs text-gray-400">{card.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex border-b border-gray-100 px-6 pt-4">
              {[
                { key: 'pendentes', label: `Pendentes (${pendingFisioterapeutas.length})` },
                { key: 'historico', label: 'Histórico' },
              ].map(tab => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                  className={`pb-3 px-1 mr-6 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Pendentes */}
            {activeTab === 'pendentes' && (
              <div className="p-6">
                {pendingFisioterapeutas.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mb-3">
                      <CheckCircle className="w-7 h-7 text-green-500" />
                    </div>
                    <p className="text-sm font-medium text-gray-700">Nenhum pendente</p>
                    <p className="text-xs text-gray-400 mt-1">Todas as solicitações foram analisadas</p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    {pendingFisioterapeutas.map((f) => (
                      <div key={f._id} className="border border-gray-100 rounded-xl p-4 hover:border-gray-200 transition-all">
                        <div className="flex items-start gap-3 mb-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${f.role === 'clinica' ? 'bg-blue-100' : 'bg-blue-100'}`}>
                            {f.role === 'clinica'
                              ? <Building2 className="w-5 h-5 text-blue-600" />
                              : <Users className="w-5 h-5 text-blue-600" />
                            }
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-gray-900 text-sm truncate">
                                {f.role === 'clinica' ? (f.clinicaInfo?.nome || f.nome) : f.nome}
                              </p>
                              {f.role === 'clinica' && (
                                <span className="flex-shrink-0 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Clínica</span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 truncate">{f.email}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {f.role === 'clinica'
                                ? <>CNPJ: <span className="font-medium text-gray-700">{f.clinicaInfo?.cnpj || '—'}</span></>
                                : <>CREFITO: <span className="font-medium text-gray-700">{f.crefito}</span></>
                              }
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">Cadastrado em {formatDateBR(f.createdAt)}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => handleApprove(f._id)} className="flex-1 flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 text-white py-2 rounded-xl text-sm font-medium transition-colors">
                            <Check className="w-4 h-4" /> {f.role === 'clinica' ? 'Aprovar Clínica' : 'Aprovar'}
                          </button>
                          <button onClick={() => handleReject(f._id)} className="flex-1 flex items-center justify-center gap-1.5 bg-red-500 hover:bg-red-600 text-white py-2 rounded-xl text-sm font-medium transition-colors">
                            <X className="w-4 h-4" /> Rejeitar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Histórico */}
            {activeTab === 'historico' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">Histórico</h2>
                    <p className="text-xs text-gray-500">Fisioterapeutas e clínicas cadastrados no sistema.</p>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input type="text" placeholder="Buscar..." value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none w-48" />
                  </div>
                </div>

                {filteredHistory.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                      <Users className="w-7 h-7 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">Nenhum histórico disponível</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase pb-3 pr-4">Nome</th>
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase pb-3 pr-4">Email</th>
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase pb-3 pr-4">CREFITO / CNPJ</th>
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase pb-3 pr-4">Status</th>
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase pb-3 pr-4">Data de Decisão</th>
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase pb-3">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {filteredHistory.map((f) => {
                          const isClinica = f.role === 'clinica';
                          const nome = isClinica ? (f.clinicaInfo?.nome || f.nome) : f.nome;
                          const status = getStatus(f);
                          return (
                            <tr key={f._id} className="hover:bg-gray-50 transition-colors">
                              <td className="py-3 pr-4">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                    {isClinica
                                      ? <Building2 className="w-4 h-4 text-blue-600" />
                                      : <Users className="w-4 h-4 text-blue-600" />
                                    }
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-gray-900">{nome}</span>
                                    {isClinica && <span className="ml-1.5 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Clínica</span>}
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 pr-4 text-sm text-gray-500">{f.email}</td>
                              <td className="py-3 pr-4 text-sm text-gray-700 font-medium">
                                {isClinica ? (f.clinicaInfo?.cnpj || '—') : (f.crefito || '—')}
                              </td>
                              <td className="py-3 pr-4"><StatusBadge status={status} /></td>
                              <td className="py-3 pr-4 text-sm text-gray-500">
                                {f.decisaoAt ? formatDateTimeBR(f.decisaoAt) : '—'}
                              </td>
                              <td className="py-3">
                                <div className="flex items-center gap-2">
                                  <button onClick={() => setModalItem(f)} title="Ver detalhes"
                                    className="w-8 h-8 rounded-lg bg-blue-50 hover:bg-blue-100 flex items-center justify-center transition-colors">
                                    <Eye className="w-4 h-4 text-blue-600" />
                                  </button>
                                  {status === 'aprovado' && (
                                    <button onClick={() => handleBlock(f._id)} title="Bloquear"
                                      className="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors">
                                      <Lock className="w-4 h-4 text-red-500" />
                                    </button>
                                  )}
                                  {status === 'rejeitado' && (
                                    <button onClick={() => handleUnblock(f._id)} title="Desbloquear"
                                      className="w-8 h-8 rounded-lg bg-green-50 hover:bg-green-100 flex items-center justify-center transition-colors">
                                      <Unlock className="w-4 h-4 text-green-600" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Resumo do Sistema</h3>
              <span className="text-xs text-gray-400">
                {new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'America/Recife' })}
              </span>
            </div>
            <div className="space-y-3">
              {[
                { label: 'Aprovados', value: totalAprovados, total: totalFisios, color: 'bg-green-500' },
                { label: 'Pendentes', value: pendingFisioterapeutas.length, total: totalFisios, color: 'bg-amber-400' },
                { label: 'Bloqueados', value: totalRejeitados, total: totalFisios, color: 'bg-red-400' },
              ].map((item) => (
                <div key={item.label}>
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>{item.label}</span>
                    <span className="font-medium">{item.value} ({totalFisios > 0 ? Math.round((item.value / totalFisios) * 100) : 0}%)</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className={`${item.color} h-2 rounded-full transition-all`} style={{ width: totalFisios > 0 ? `${(item.value / totalFisios) * 100}%` : '0%' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center">
                <span className="text-lg">💡</span>
              </div>
              <h3 className="font-semibold text-gray-900">Dica do Dia</h3>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              Mantenha os dados dos fisioterapeutas e clínicas sempre atualizados para um melhor controle.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;